function isHexColor(color) {
    return /^#[0-9a-fA-F]{6}$/.test(color);
}
// --- Tailwind color utilities ---
function getColorNumericValue(color) {
    if (color === "white")
        return 50;
    if (color === "black")
        return 950;
    const match = color.match(/-(\d+)$/);
    return match && match[1] ? parseInt(match[1]) : 500;
}
function getBaseColorName(color) {
    return color.replace(/-\d+$/, "");
}
function createColorWithValue(baseColor, value) {
    const baseName = getBaseColorName(baseColor);
    if (baseName === "white" || baseName === "black")
        return baseName;
    return `${baseName}-${value}`;
}
function hasGoodContrast(color1, color2) {
    return Math.abs(getColorNumericValue(color1) - getColorNumericValue(color2)) >= 500;
}
function findContrastingColor(targetColor, availableColors) {
    for (const color of availableColors) {
        if (hasGoodContrast(targetColor, color))
            return color;
    }
    return null;
}
function generateContrastingColor(targetColor, availableColors) {
    const targetValue = getColorNumericValue(targetColor);
    const baseNames = [...new Set(availableColors.map(getBaseColorName))];
    const contrastValue = targetValue <= 400
        ? Math.max(900, targetValue + 500)
        : Math.min(100, Math.max(50, targetValue - 500));
    for (const baseName of baseNames) {
        const candidateColor = createColorWithValue(baseName, contrastValue);
        if (hasGoodContrast(targetColor, candidateColor))
            return candidateColor;
    }
    if (baseNames.length > 0)
        return createColorWithValue(baseNames[0], contrastValue);
    return targetValue <= 400 ? "slate-950" : "slate-50";
}
// --- Hex color utilities ---
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result || !result[1] || !result[2] || !result[3])
        return null;
    return { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) };
}
function getLuminance(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb)
        return 0;
    const toLinear = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}
function getContrastRatio(hex1, hex2) {
    const lum1 = getLuminance(hex1);
    const lum2 = getLuminance(hex2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
}
function hasGoodHexContrast(hex1, hex2) {
    return getContrastRatio(hex1, hex2) >= 4.5;
}
function findContrastingHexColor(targetHex, availableHexColors) {
    for (const hex of availableHexColors) {
        if (hasGoodHexContrast(targetHex, hex))
            return hex;
    }
    return null;
}
// --- Shared shuffle utility ---
function shuffleArray(array) {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = newArray[i];
        newArray[i] = newArray[j];
        newArray[j] = temp;
    }
    return newArray;
}
// --- Color pair assignment (shared between Tailwind and hex) ---
function getColorPairs(keys) {
    const foregroundKeys = keys.filter((k) => k.toLowerCase().includes("foreground"));
    const backgroundKeys = keys.filter((k) => k.toLowerCase().includes("background"));
    const otherKeys = keys.filter((k) => !k.toLowerCase().includes("foreground") && !k.toLowerCase().includes("background"));
    const pairs = [];
    const usedFg = new Set();
    const usedBg = new Set();
    for (const fgKey of foregroundKeys) {
        if (usedFg.has(fgKey))
            continue;
        const fgPrefix = fgKey.replace(/foreground/i, "");
        let bgKey = backgroundKeys.find((k) => !usedBg.has(k) && k.replace(/background/i, "") === fgPrefix);
        if (!bgKey)
            bgKey = backgroundKeys.find((k) => !usedBg.has(k));
        if (bgKey) {
            pairs.push({ fg: fgKey, bg: bgKey });
            usedFg.add(fgKey);
            usedBg.add(bgKey);
        }
    }
    const unpaired = [
        ...foregroundKeys.filter((k) => !usedFg.has(k)),
        ...backgroundKeys.filter((k) => !usedBg.has(k)),
        ...otherKeys,
    ];
    return { pairs, unpaired };
}
// --- Public API ---
export function generateNewColorsFromHex(defaultColors, availableHexColors) {
    const keys = Object.keys(defaultColors);
    const nextColors = {};
    for (const k of keys)
        nextColors[k] = defaultColors[k];
    const { pairs, unpaired } = getColorPairs(keys);
    const usedColors = new Set();
    for (const pair of pairs) {
        const shuffled = shuffleArray(availableHexColors.filter((c) => !usedColors.has(c)));
        if (shuffled.length >= 2) {
            const bgColor = shuffled[0];
            let fgColor = findContrastingHexColor(bgColor, shuffled.slice(1));
            if (!fgColor) {
                let bestContrast = 0;
                let bestCandidate = null;
                for (const candidate of shuffled.slice(1)) {
                    const contrast = getContrastRatio(bgColor, candidate);
                    if (contrast > bestContrast) {
                        bestContrast = contrast;
                        bestCandidate = candidate;
                    }
                }
                fgColor = bestCandidate || shuffled[1];
            }
            nextColors[pair.bg] = bgColor;
            nextColors[pair.fg] = fgColor;
            usedColors.add(bgColor);
            usedColors.add(fgColor);
        }
        else if (shuffled.length === 1) {
            const bgColor = shuffled[0];
            const fgColor = findContrastingHexColor(bgColor, availableHexColors.filter((c) => c !== bgColor))
                || availableHexColors.find((c) => c !== bgColor)
                || availableHexColors[0];
            nextColors[pair.bg] = bgColor;
            nextColors[pair.fg] = fgColor;
            usedColors.add(bgColor);
        }
    }
    const remaining = shuffleArray(availableHexColors.filter((c) => !usedColors.has(c)));
    unpaired.forEach((key, index) => {
        nextColors[key] = remaining.length > 0
            ? remaining[index % remaining.length]
            : availableHexColors[index % availableHexColors.length];
    });
    return nextColors;
}
export function generateNewColors(defaultColors, currentSchemeColors) {
    if (currentSchemeColors.some(isHexColor)) {
        return generateNewColorsFromHex(defaultColors, currentSchemeColors);
    }
    const keys = Object.keys(defaultColors);
    const nextColors = {};
    for (const k of keys)
        nextColors[k] = defaultColors[k];
    const { pairs, unpaired } = getColorPairs(keys);
    const usedColors = new Set();
    for (const pair of pairs) {
        const shuffled = shuffleArray(currentSchemeColors.filter((c) => !usedColors.has(c)));
        if (shuffled.length >= 2) {
            const bgColor = shuffled[0];
            const fgColor = findContrastingColor(bgColor, shuffled.slice(1))
                || generateContrastingColor(bgColor, currentSchemeColors);
            nextColors[pair.bg] = bgColor;
            nextColors[pair.fg] = fgColor;
            usedColors.add(bgColor);
            if (currentSchemeColors.includes(fgColor))
                usedColors.add(fgColor);
        }
        else if (shuffled.length === 1) {
            const bgColor = shuffled[0];
            nextColors[pair.bg] = bgColor;
            nextColors[pair.fg] = generateContrastingColor(bgColor, currentSchemeColors);
            usedColors.add(bgColor);
        }
    }
    const remaining = shuffleArray(currentSchemeColors.filter((c) => !usedColors.has(c)));
    unpaired.forEach((key, index) => {
        nextColors[key] = remaining.length > 0
            ? remaining[index % remaining.length]
            : currentSchemeColors[index % currentSchemeColors.length];
    });
    return nextColors;
}
